import { supabase } from "./client";
import { supabaseUrl } from "./config";
import { withRequestDeadline } from "./requestDeadline";

const cache = new Map();
const pending = new Map();
const references = new Map();
const referenceRequests = new Map();
const queue = [];
const MAX_IMAGE_BYTES = 24 * 1024 * 1024;
let cacheBytes = 0;
let active = 0;
const keyFor = (product) => JSON.stringify([
  product.imageScope || "", product.imageMenuId, product.id, product.imageVersion || "",
]);
const referenceKey = (product) => JSON.stringify([product.imageScope || "", product.imageMenuId]);
const removeCached = (key) => {
  cacheBytes -= cache.get(key)?.url?.length || 0;
  cache.delete(key);
};

export const invalidateMenuProductImage = (menuId, productId) => {
  for (const key of new Set([...cache.keys(), ...pending.keys()])) {
    const [, menu, product] = JSON.parse(key);
    if (menu !== menuId || product !== productId) continue;
    removeCached(key);
    pending.delete(key);
  }
  for (const key of new Set([...references.keys(), ...referenceRequests.keys()])) {
    if (JSON.parse(key)[1] !== menuId) continue;
    references.delete(key);
    referenceRequests.delete(key);
  }
};

export const getCachedMenuProductImage = (product) => {
  const key = keyFor(product);
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) {
    removeCached(key);
    return undefined;
  }
  cache.delete(key);
  cache.set(key, entry);
  return entry.url;
};

const resolveReference = (reference) => {
  const value = String(reference || "").trim();
  const match = value.match(/^mozzoapp-public-image:(product):([0-9a-f-]{36}):([a-f0-9]{16,64})$/i);
  if (!match) return value || null;
  const [, kind, id, version] = match;
  return `${supabaseUrl}/functions/v1/public-menu-image?${new URLSearchParams({ kind, id, v: version })}`;
};

const loadReferences = (product) => {
  const key = referenceKey(product);
  const saved = references.get(key);
  if (saved && saved.expiresAt > Date.now()) return Promise.resolve(saved.rows);
  if (referenceRequests.has(key)) return referenceRequests.get(key);
  const request = withRequestDeadline(async (signal) => {
    const { data, error } = await supabase.rpc("get_menu_product_image_references", { p_menu_id: product.imageMenuId }).abortSignal(signal);
    if (error) {
      // Compatibility during rollout only; never bypass permission/network errors.
      if (error.code === "PGRST202" || error.code === "42883") return null;
      throw new Error(error.message || "No se pudieron consultar las fotos.");
    }
    const rows = new Map((data || []).map((row) => [row.product_id, resolveReference(row.image_reference)]));
    if (!signal.aborted && referenceRequests.get(key) === request) {
      references.set(key, { rows, expiresAt: Date.now() + 60000 });
      while (references.size > 30) references.delete(references.keys().next().value);
    }
    return rows;
  }, { timeoutMs: 15000, message: "La consulta de fotos tardo demasiado." })
    .finally(() => { if (referenceRequests.get(key) === request) referenceRequests.delete(key); });
  referenceRequests.set(key, request);
  return request;
};

const preloadPhoto = (url) => withRequestDeadline((signal) => new Promise((resolve, reject) => {
  const image = new window.Image();
  const finish = (error) => {
    image.onload = null;
    image.onerror = null;
    signal.removeEventListener("abort", cancel);
    if (error) { image.src = ""; reject(error); } else resolve(url);
  };
  const cancel = () => finish(new Error("Se interrumpio la descarga de la foto."));
  image.onload = () => finish();
  image.onerror = () => finish(new Error("No se pudo descargar la foto."));
  signal.addEventListener("abort", cancel, { once: true });
  image.src = url;
}), { timeoutMs: 45000, message: "La foto tardo demasiado en cargar." });

const releaseSlot = () => { active -= 1; drain(); };
const drain = () => {
  // Bound actual photo downloads, not just their small metadata requests.
  while (active < 2 && queue.length) {
    active += 1;
    const { run, resolve, reject } = queue.shift();
    run().then(resolve, reject).finally(releaseSlot);
  }
};

export const loadMenuProductImage = (product) => {
  if (!product.imageMenuId || !product.id) return Promise.resolve(null);
  const existing = getCachedMenuProductImage(product);
  if (existing !== undefined) return Promise.resolve(existing);
  const key = keyFor(product);
  if (pending.has(key)) return pending.get(key);

  const request = new Promise((resolve, reject) => {
    queue.push({ resolve, reject, run: async () => {
      const rows = await loadReferences(product);
      let url;
      if (rows) {
        url = rows.get(product.id) || null;
      } else {
        const result = await withRequestDeadline((signal) => supabase.from("products")
          .select("image_url").eq("menu_id", product.imageMenuId).eq("id", product.id)
          .maybeSingle().abortSignal(signal), { timeoutMs: 45000, message: "La foto tardo demasiado en cargar." });
        if (result.error) throw new Error(result.error.message || "No se pudo cargar la foto.");
        url = result.data?.image_url || null;
      }
      if (url) await preloadPhoto(url);
      if (pending.get(key) === request && (url?.length || 0) <= MAX_IMAGE_BYTES) {
        removeCached(key);
        cache.set(key, { url, expiresAt: Date.now() + 60000 });
        cacheBytes += url?.length || 0;
        while (cacheBytes > MAX_IMAGE_BYTES || cache.size > 100) removeCached(cache.keys().next().value);
      }
      return url;
    } });
  }).finally(() => { if (pending.get(key) === request) pending.delete(key); });
  pending.set(key, request);
  drain();
  return request;
};
